import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { MdSearch } from "react-icons/md";
import { toast } from "react-toastify";
import Header from "../../../components/user/Header";
import Footer from "../../../components/user/Footer";
import ListBerita from "../../../components/user/berita/CardBerita";

function Berita() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoading(true);
          setTimeout(() => setLoading(false), 800);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`/api_w/postcategories`, {
          headers: {
            db_name: process.env.REACT_APP_DB_NAME,
          },
        });
        setCategories(response.data.data);
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const SkeletonBox = ({ className }) => (
    <div className={`animate-pulse rounded bg-gray-200 ${className}`} />
  );

  const WaveBackground = ({ children }) => (
    <section className="relative min-h-[600px] overflow-hidden bg-[#0b5c2a]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1c9b4a] via-[#087236] to-[#063717]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_45%_35%,rgba(44,185,92,0.35),transparent_42%)]" />

      {children}

      <svg
        className="absolute bottom-0 left-0 h-[350px] w-full md:h-[350px]"
        viewBox="0 0 1440 220"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          fill="#fff"
          d="M0,176 C210,204 415,168 636,140 C870,110 1025,112 1212,140 C1305,154 1375,164 1440,162 L1440,220 L0,220 Z"
        />
      </svg>
    </section>
  );

  const SidebarSkeleton = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <SkeletonBox className="mb-4 h-5 w-24" />
        <SkeletonBox className="h-10 w-full rounded-xl" />
      </div>
      <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <SkeletonBox className="h-5 w-28" />
        {[1, 2, 3, 4].map((i) => (
          <button key={i} className="w-full">
            <SkeletonBox className="h-8 w-full rounded-full" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Header />

      {/* Banner Header */}
      <WaveBackground>
        <div className="relative z-10 mx-auto flex min-h-[600px] w-full max-w-7xl flex-col items-center justify-center px-5 pb-24 pt-28 text-center text-white md:pb-32 md:pt-24">
          <h1 className="max-w-8xl text-5xl font-bold leading-tight tracking-normal md:text-8xl">
            Berita
          </h1>

          <p className="mt-3 text-base font-normal text-white/90 md:text-2xl">
            Berita Terbaru tentang Digitalisasi Madrasah Jawa Tengah
          </p>
        </div>
      </WaveBackground>

      {/* Content Section */}
      <div className="mx-auto max-w-8xl px-4 py-10" ref={sectionRef}>
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Main Content - List Berita */}
          <div className="w-full lg:w-[60%]">
            {loading ? (
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
                  >
                    <SkeletonBox className="h-56 w-full" />
                    <div className="space-y-3 p-6">
                      <SkeletonBox className="h-3 w-1/4" />
                      <SkeletonBox className="h-6 w-3/4" />
                      <SkeletonBox className="h-4 w-full" />
                      <SkeletonBox className="h-4 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ListBerita
                selectedCategory={selectedCategory}
                searchQuery={searchQuery}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-[35%]">
            {loading ? (
              <SidebarSkeleton />
            ) : (
              <>
                {/* Search Box */}
                <div className="mb-8 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                  <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-800">
                    Cari Berita
                  </h3>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Cari berita..."
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pr-10 text-sm text-gray-700 placeholder-gray-400 transition-all duration-200 focus:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-500/30"
                    />
                    <button className="absolute right-1.5 top-1.5 rounded-lg bg-green-600 p-2 text-white transition-colors duration-200 hover:bg-green-700">
                      <MdSearch className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Kategori Berita */}
                <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
                  <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-800">
                    <div className="h-5 w-1 rounded-full bg-green-500" />
                    Kategori Berita
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleCategoryFilter("")}
                      className={`inline-block cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                        selectedCategory === ""
                          ? "border border-green-600 bg-green-600 text-white"
                          : "border border-gray-200 bg-gray-50 text-gray-600 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                      }`}
                    >
                      Semua
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryFilter(category.id.toString())}
                        className={`inline-block cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                          selectedCategory === category.id.toString()
                            ? "border border-green-600 bg-green-600 text-white"
                            : "border border-gray-200 bg-gray-50 text-gray-600 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default Berita;
